import axios from 'axios'
import toast from 'react-hot-toast'

import { useAuthStore } from '../store/authStore'
import { clearAllCaches } from '../store/clearAllCaches'

// Dynamically select API URL based on environment
export const getApiUrl = () => {
  const env = import.meta.env.VITE_ENV || 'development'

  if (env === 'production') {
    return 'https://bhrigugpt.com'
  } else {
    return 'http://localhost:9200'
  }
}

const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000, // 30s default timeout (SSE endpoints handle their own)
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth token if using JWT
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // If the request data is FormData, remove the default Content-Type header
    // to let the browser set it with the proper boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle errors globally
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 400:
          // Let the calling component handle 400 errors
          break;
        case 401:
          // Unauthorized - redirect to login unless it's the login request itself
          if (!error.config.url?.includes('/api/auth/login') && !error.config.url?.includes('/api/auth/google-callback')) {
            // Clear all caches and auth state before redirecting
            clearAllCaches()
            useAuthStore.getState().logout()
            toast.error('Session expired. Please login again.')
            window.location.href = '/'
          }
          break;
        case 403:
          toast.error('You do not have permission to perform this action.')
          break
        case 404:
          // Check if the request explicitly suppresses 404 toasts
          // @ts-ignore
          if (!error.config?.suppress404) {
            toast.error('Resource not found.')
          }
          break
        case 429:
          toast.error(data?.error || 'Too many requests. Please wait and try again.')
          break
        case 500:
          toast.error('Server error. Please try again later.')
          break
        default:
          toast.error(data?.error || 'An error occurred.')
      }
    } else if (error.request) {
      // Network error
      toast.error('Network error. Please check your connection.')
    }

    return Promise.reject(error)
  }
)

export default api
